import React, { useContext, useEffect, useRef, useState, KeyboardEvent } from 'react'
import { useAppConfig } from '../../../../hooks/useAppConfig'
import styled from 'styled-components'
import { color, compose, textStyle } from 'styled-system'
import * as yup from 'yup'
import { GridItem } from '@zuto/core/src/grid'
import { Edit, Search } from '@zuto/icons'
import { Button as OldButton, Option } from '@zuto/atoms'
import Box from '@zuto/core/src/box'
import { Loading } from '@zuto/molecules'
import { useCallbackOnce } from '@zuto/utilities'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import MuiSelect from '@zuto/atoms/src/Mui/Select/MuiSelect'
import { SelectChangeEvent } from '@mui/material/Select'
import $console from '@zuto/utilities/src/$console'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import postcodeService from '../../../../services/postcode-service'
import AppFormQuestion from '../../../AppFormQuestion'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import QuestionKey from '../../../../Questions/question-key'
import ErrorModal from '../../../ErrorModal'
import AddressManualInput from '../AddressManualInput'
import { AddressDetails, AddressEntryErrors } from '../AddressHistoryContext'
import { validateEntry } from '../AddressHistoryValidation'
import AddressHistoryEntryContext from '../AddressEntry/AddressHistoryEntryContext'
import NavigationButtons from '../../../NavigationButtons/NavigationButtons'
import { questionFontStyle, fireReactGAEvent } from '../../utils'
import { sortAddressList } from '../../../../utils/utils'
import { AttributionContext } from '../../../../context'
import withFade from '../../../HOC/withFade'
import { AmplitudeEvents, trackAmplitudeEvent } from '../../../../utils/amplitude-utils'
import { toSnakeCase } from '../../../../utils/string-utils'
import { PrefillReviewPageApplicationFormContext } from '../../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { UpdateFollowingPrefillStepType } from '../../../../Questions/question-component-props'
import Button from '@zuto/atoms/src/Mui/Button/Button'
import { useAppFormEventsService } from '../../../../hooks/useAppFormEventsService'

interface AddressDetailsProps {
  existingDetails?: AddressDetails
  entryId: string
  disablePrevious?: boolean
  updateFollowingPrefillStep?: UpdateFollowingPrefillStepType
}

type ManualEntryProps = {
  $appearDisabled?: boolean
}

const ManualEntryButton = styled(OldButton)<ManualEntryProps>`
  /* This is due to the iPhone sticky hover (Native bug) */
  @media only screen and (max-width: 767px) {
    &:hover {
      color: ${(props) => (props.$appearDisabled ? props.theme.colors.material[1] : props.theme.colors.secondary[1])};
      svg {
        path {
          fill: ${(props) => (props.$appearDisabled ? props.theme.colors.material[1] : props.theme.colors.secondary[1])};
        }
      }
    }
    svg {
      &:hover {
        path {
          fill: ${(props) => (props.$appearDisabled ? props.theme.colors.material[1] : props.theme.colors.secondary[1])};
        }
      }
    }
  }
  min-width: auto;
  svg {
    order: 1;
    path {
      fill: ${(props) => (props.$appearDisabled ? props.theme.colors.material[1] : props.theme.colors.secondary[1])};
    }
  }
  ${(props) => (props.$appearDisabled ? compose(color)({ ...props, color: 'material.1' }) : compose(color)({ ...props, color: 'secondary.1' }))}
  ${(props) => compose(textStyle)({ ...props, textStyle: 'body.current.regular' })}
`

type DisplayState = 'SEARCH_RESULTS' | 'MANUAL_ENTRY'

const AddressDetailsQuestionV1 = ({ existingDetails = {}, entryId, disablePrevious = false, updateFollowingPrefillStep }: AddressDetailsProps) => {
  const appConfig = useAppConfig()
  const attribution = useContext(AttributionContext)
  const { featureSwitches } = useContext(ApplicationFormContext)
  const eventsService = useAppFormEventsService()
  const { reviewPageV3, questionCopyChangeV1 } = featureSwitches ?? {}

  const { dispatch } = useContext(AddressHistoryEntryContext)
  const { dispatch: appFormContextDispatch } = useContext(ApplicationFormContext)
  const { hasNavigatedFromRequiredClick, setHasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const [postcodeSearch, setPostcodeSearch] = useState(existingDetails.postcode ?? '')
  const [searchError, setSearchError] = useState('')
  const [addressList, setAddressList] = useState<Option[]>()
  const [displayState, setDisplayState] = useState<DisplayState | undefined>(existingDetails.buildingName || existingDetails.buildingNumber || existingDetails.flatNumber ? 'MANUAL_ENTRY' : undefined)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [postcodeSearchCb, resetPostcodeSearchCb] = useCallbackOnce()
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  const [addressDetails, setAddressDetails] = useState<AddressDetails>(existingDetails)
  const [errors, setErrors] = useState<AddressEntryErrors>({})
  const [nextButtonCb, resetNextButtonCb] = useCallbackOnce()
  const [previousButtonCb, resetPreviousButtonCb] = useCallbackOnce()
  const canContinue = !!(displayState === 'SEARCH_RESULTS' && addressDetails.id) || (displayState === 'MANUAL_ENTRY' && (addressDetails.postcode || addressDetails.id))
  const [manualAddressAnswers, setManualAddressAnswers] = useState<AddressDetails>(existingDetails)
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()

  useEffect(() => {
    const label = `qst.addressHistory.${parseInt(entryId, 10) - 1}.postcodeAnywhere`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })
    trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(label) })
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- writes the searched postcode into the addressDetails object the parent reads
    addressDetails.postcode = postcodeSearch
  }, [postcodeSearch])

  const isValidUKPostcode = (postcode: string): boolean => {
    // Remove spaces from the input postcode
    const cleanedPostcode = postcode.replace(/\s/g, '')

    return yup
      .string()
      .required('Please enter a postcode')
      .matches(/^([Gg][Ii][Rr]0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))[0-9][A-Za-z]{2})$/)
      .max(20)
      .label('Postcode')
      .isValidSync(cleanedPostcode)
  }

  const search = (postcode: string) => {
    if (!isValidUKPostcode(postcode)) {
      setSearchError('Please enter a valid UK postcode')
      resetPostcodeSearchCb()
      return
    }
    setSearchError('')
    setLoading(true)
    postcodeService(appConfig)
      .getAddresses(postcode)
      .then((results) => {
        const sortedAddressList = sortAddressList(results)
        setAddressList([
          {
            value: '',
            label: 'Select an address',
            disabled: true
          },
          ...sortedAddressList
        ])
        setDisplayState('SEARCH_RESULTS')

        try {
          zutoAnalyticsHelper.sendCustomEvent({
            eventType: 'loqateFind - addressDetailsV1',
            eventCategory: 'Loqate',
            experiment: 'AddressDetailsV1',
            eventMessage: 'Success'
          })
        } catch (error) {
          $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
        }
      })
      .catch((error) => {
        setApiError(true)
        try {
          zutoAnalyticsHelper.sendCustomEvent({
            eventType: 'loqateFind Failed - addressDetailsV1',
            eventCategory: 'Loqate',
            experiment: 'AddressDetailsV1',
            eventMessage: error ? error : 'loqateFind failed'
          })
        } catch (error) {
          $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
        }
      })
      .finally(() => {
        setLoading(false)
        resetPostcodeSearchCb()
      })
  }

  const getSelectedAddress = async (id: string): Promise<AddressDetails> => postcodeService(appConfig).retrieveAddress(id, 'AddressDetailsV1')

  const postcodeSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.currentTarget.value ? e.currentTarget.value : ''
    if (isValidUKPostcode(postcode)) {
      setSearchError('')
    }
    setPostcodeSearch(postcode)
  }

  const showManualEntry = () => {
    if (displayState !== 'MANUAL_ENTRY') {
      const manualLabel = `qst.addressHistory.${parseInt(entryId, 10) - 1}.manualAddress`
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Question Visible',
        label: manualLabel,
        nonInteraction: true
      })
      eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: manualLabel })
      trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(manualLabel) })
      const resetAddressDetails = {
        postcode: postcodeSearch
      }
      setManualAddressAnswers(resetAddressDetails)
      setAddressDetails(resetAddressDetails)
      setErrors({})
      setDisplayState('MANUAL_ENTRY')
    }
  }

  const closeError = () => {
    setApiError(false)
    setDisplayState('MANUAL_ENTRY')
    if (searchButtonRef.current) {
      searchButtonRef.current.focus()
    }
  }

  const handleEnterKeyPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter' || e.key === 'Enter') {
      postcodeSearchCb(() => search(postcodeSearch))
    }
  }

  const checkShouldSearchButtonBeDisabled = () => !postcodeSearch || loading

  const validate = (): boolean => {
    const result = validateEntry({ addressDetails, entrySource: 'FORM' }, attribution, false, true, false)
    if (typeof result === 'boolean') return result
    const prettyErrors: Record<string, string> = {}
    result.forEach((validationError) => {
      if (!validationError.path) return
      const [errorMessage] = validationError.errors
      prettyErrors[validationError.path.toString()] = errorMessage
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Validation Error',
        label: validationError.path.toString(),
        nonInteraction: true
      })
    })
    setErrors(prettyErrors)
    return false
  }

  const saveAnswers = (answers: AddressDetails) => {
    const updatedAnswers = { ...answers }

    // Check if the 'postcode' key exists in the object and is a string
    if (typeof updatedAnswers.postcode === 'string') {
      // Remove spaces from the 'postcode' value
      updatedAnswers.postcode = updatedAnswers.postcode.replace(/\s/g, '')
    }

    dispatch({
      type: 'UPDATE_ADDRESS_DETAILS',
      value: displayState !== 'MANUAL_ENTRY' ? answers : updatedAnswers
    })
  }

  const previousHandler = () => {
    if (reviewPageV3 && !validate() && displayState === 'MANUAL_ENTRY') {
      resetPreviousButtonCb()
      return
    }
    const partialAnswer = addressDetails.postcode ? addressDetails : { postcode: postcodeSearch }
    saveAnswers(partialAnswer)
    dispatch({
      type: 'PREVIOUS'
    })
    appFormContextDispatch({
      type: 'NAVIGATED_FROM_QUESTION',
      questionName: displayState !== 'MANUAL_ENTRY' ? `addressPostcodeEntry-${parseInt(entryId, 10)}` : `manualAddressEntry-${parseInt(entryId, 10)}`,
      lastNavAction: 'PREVIOUS'
    })

    setTimeout(() => {
      if (updateFollowingPrefillStep) {
        if (entryId === '1' || (reviewPageV3 && hasNavigatedFromRequiredClick)) {
          updateFollowingPrefillStep(false, false, true)
        }
      }
    }, 50)

    if (reviewPageV3) {
      setHasNavigatedFromRequiredClick(false)
    }
  }

  const searchNextHandler = () => {
    if (!addressDetails.id) {
      resetNextButtonCb()
      return
    }
    getSelectedAddress(addressDetails.id).then((addressDetailsResult) => {
      saveAnswers(addressDetailsResult)
      dispatch({
        type: 'NEXT'
      })
    })
  }

  const manualNextHandler = () => {
    if (!validate()) {
      resetNextButtonCb()
      return
    }
    saveAnswers(addressDetails)
    dispatch({
      type: 'NEXT'
    })
  }

  const nextHandler = () => {
    switch (displayState) {
      case 'MANUAL_ENTRY':
        manualNextHandler()
        break
      case 'SEARCH_RESULTS':
        searchNextHandler()
        break
      default:
    }
  }

  const muiDropdownOnChange = (e: SelectChangeEvent) => {
    setAddressDetails({
      id: e.target.value
    })
  }

  const updateManualAddressDetails = (name: string, value: string) => {
    setAddressDetails({
      ...addressDetails,
      [name]: value
    })
  }

  const renderQuestionTitle = () => {
    const isFirstEntry = entryId === '1'
    if (questionCopyChangeV1 && isFirstEntry) {
      return "What's your current address?"
    }

    return `What ${isFirstEntry ? 'is your address?' : 'was your previous address?'}`
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="186px"
      >
        <AppFormQuestion
          questionKey={`${QuestionKey.ADDRESS_HISTORY}.address`}
          mt={3}
          mb={2}
          hintText={'We need your address history for the last 3 years. This is so our lenders can identify you and link to your credit history.'}
          textStyle={questionFontStyle}
        >
          {renderQuestionTitle()}
        </AppFormQuestion>
        <GridItem>
          <GridItem gridColumn={['auto / span 9', '1 / span 6']}>
            <MuiTextField
              aria-label="label-postcode-search"
              autoFocus={!reviewPageV3}
              value={postcodeSearch}
              errorMessage={searchError}
              id="postcode-search"
              inputMaxLength={20}
              inputType="text"
              inputVariant="outlined"
              label="Enter your full postcode"
              mt="12px"
              name="postcode-search"
              onChangeHandler={postcodeSearchChange}
              onKeyPressHandler={handleEnterKeyPress}
              testId="postcode-search-input"
            />
          </GridItem>
          <GridItem gridColumn={['auto / span 12', '7 / span 6']}>
            <Box
              mb={13}
              mt={13}
            >
              <Button
                variant="outlined"
                endSvgIcon={Search}
                data-testid="find-address-button"
                onClick={() => postcodeSearchCb(() => search(postcodeSearch))}
                disabled={checkShouldSearchButtonBeDisabled()}
                ref={searchButtonRef}
                sx={{
                  justifyContent: 'space-between',
                  '& .MuiSvgIcon-root': {
                    marginRight: '16px !important'
                  }
                }}
              >
                Find address
              </Button>
              {loading && <Loading />}
            </Box>
          </GridItem>
          {displayState === 'SEARCH_RESULTS' && addressList && (
            <GridItem
              gridColumn="1 / span 12"
              style={{ overflow: 'hidden' }}
              mt={3}
              mb={5}
            >
              <MuiSelect
                aria-label="label-address-search-results"
                data={addressList.filter((item) => item.label !== 'Select an address')}
                defaultValue=""
                id="address-search-results"
                label="Your address"
                name="address-search-results"
                onChangeHandler={muiDropdownOnChange}
                testId="address-dropdown"
                variant="outlined"
              />
            </GridItem>
          )}
          <GridItem
            gridColumn="1 / span 12"
            style={{ overflow: 'hidden' }}
            mb={13}
            mt={0}
          >
            <ManualEntryButton
              mb={0}
              p={0}
              variant="tertiary"
              icon={<Edit />}
              onClick={showManualEntry}
              data-testid="show-manual-entry"
              $appearDisabled
            >
              Or enter address manually
            </ManualEntryButton>
          </GridItem>
          {displayState === 'MANUAL_ENTRY' && (
            <AddressManualInput
              valueChanged={updateManualAddressDetails}
              postcode={postcodeSearch}
              setPostcode={postcodeSearchChange}
              error={errors}
              existingAnswers={manualAddressAnswers}
              handleKeyPressEvent={manualNextHandler}
            />
          )}
        </GridItem>
        {apiError && <ErrorModal ctaText="Enter address" onClick={closeError}>We were unable to retrieve your address at this time, but not to worry, you can enter it manually...</ErrorModal>}
        <NavigationButtons
          nextHandler={() => nextButtonCb(nextHandler)}
          previousHandler={() => previousButtonCb(previousHandler)}
          disableNext={!canContinue}
          disablePrevious={disablePrevious ? 'invisible' : undefined}
        />
      </GridItem>
    </>
  )
}

export default withFade(AddressDetailsQuestionV1)
